import type { RefObject } from "react";
import { DateTime } from "luxon";
import { SK_DOW, SK_MON } from "../../lib/days";

const TZ = "Europe/Bratislava";

export function SlotSelector({
  typeId,
  month,
  setMonth,
  selectedDate,
  onDaySelect,
  disabled,
  slots,
  selectedSlot,
  setSlotUTC,
  slotsContainerRef,
}: {
  typeId: string;
  month: Date;
  setMonth: (d: Date) => void;
  selectedDate: Date | undefined;
  onDaySelect: (d?: Date) => void;
  disabled: { dayMatcher: (d: Date) => boolean };
  slots: string[];
  selectedSlot?: string;
  setSlotUTC: (s: string) => void;
  placement?: "horizontal" | "vertical";
  slotsContainerRef?: RefObject<HTMLDivElement | null>;
}) {
  const year = month.getFullYear();
  const monthIdx = month.getMonth();
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
  const firstDow = (new Date(year, monthIdx, 1).getDay() + 6) % 7;
  const today = new Date();

  const navBtnStyle: React.CSSProperties = {
    width: 34, height: 34,
    border: "1px solid var(--line, #DDE8F2)", background: "var(--surface, #fff)",
    borderRadius: 10, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "var(--accent, #3275CE)", fontFamily: "inherit",
  };

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 22 }}>
      {/* Calendar */}
      <div style={{ flex: "1 1 290px", minWidth: 0, background: "var(--surface, #fff)", border: "1px solid var(--line, #DDE8F2)", borderRadius: 18, padding: 18, boxShadow: "0 1px 3px var(--shadow, rgba(20,40,70,.12))" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 7 }}>
            <span style={{ fontWeight: 800, fontSize: 17, color: "var(--ink, #1A2330)" }}>{SK_MON[monthIdx]}</span>
            <span style={{ fontWeight: 600, fontSize: 15, color: "var(--ink-soft, #576678)" }}>{year}</span>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button data-testid="calendar-prev-month" style={navBtnStyle} onClick={() => setMonth(new Date(year, monthIdx - 1, 1))}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
            </button>
            <button data-testid="calendar-next-month" style={navBtnStyle} onClick={() => setMonth(new Date(year, monthIdx + 1, 1))}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
            </button>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 4 }}>
          {SK_DOW.map(d => (
            <div key={d} style={{ textAlign: "center", fontWeight: 700, fontSize: 11, color: "var(--ink-mute, #94A1B1)", padding: "4px 0" }}>{d}</div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
          {Array.from({ length: firstDow }, (_, i) => <div key={`e${i}`} />)}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const date = new Date(year, monthIdx, day);
            const isDisabled = disabled.dayMatcher(date);
            const isSelected = !!selectedDate && date.toDateString() === selectedDate.toDateString();
            const isToday = date.toDateString() === today.toDateString();
            return (
              <button
                key={day}
                data-testid={`slot-day-${day}`}
                disabled={isDisabled}
                onClick={() => onDaySelect(date)}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  aspectRatio: "1", borderRadius: 10, padding: 0,
                  fontSize: 14, fontWeight: 600, fontFamily: "inherit", width: "100%",
                  background: isSelected ? "var(--accent, #3275CE)" : "transparent",
                  color: isSelected ? "var(--accent-ink, #fff)" : isDisabled ? "var(--ink-mute, #94A1B1)" : "var(--ink, #1A2330)",
                  border: isToday && !isSelected ? "1px solid var(--accent, #3275CE)" : "1px solid transparent",
                  cursor: isDisabled ? "default" : "pointer",
                  opacity: isDisabled ? 0.38 : 1,
                }}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>

      {/* Slots */}
      <div ref={slotsContainerRef} style={{ flex: "1 1 220px", minWidth: 0, background: "var(--surface, #fff)", border: "1px solid var(--line, #DDE8F2)", borderRadius: 18, padding: 18, boxShadow: "0 1px 3px var(--shadow, rgba(20,40,70,.12))" }}>
        <div style={{ fontWeight: 800, fontSize: 16, color: "var(--ink, #1A2330)", marginBottom: 14 }}>Vyberte si termín</div>
        {slots.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 9, textAlign: "center", padding: "26px 8px", color: "var(--ink-mute, #94A1B1)" }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="3" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
            <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>Najprv si vyberte deň<br />v kalendári.</div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(70px, 1fr))", gap: 8 }}>
            {slots.map(s => {
              const label = DateTime.fromISO(s, { setZone: true }).setZone(TZ).toFormat("HH:mm");
              const isSelected = s === selectedSlot;
              return (
                <button
                  key={s}
                  onClick={() => setSlotUTC(s)}
                  data-testid={`time-slot-${label.replace(":", "-")}`}
                  style={{
                    padding: "12px 8px", borderRadius: 10,
                    border: isSelected ? "2px solid var(--accent, #3275CE)" : "1px solid var(--line, #DDE8F2)",
                    background: isSelected ? "var(--accent, #3275CE)" : "var(--surface, #fff)",
                    color: isSelected ? "var(--accent-ink, #fff)" : "var(--ink, #1A2330)",
                    fontFamily: "inherit", fontWeight: 700, fontSize: 14,
                    cursor: "pointer", textAlign: "center",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
